/**
 * Dental HMO (DHMO) skeleton
 *
 * Provides the default structure for DHMO coverage with empty benefit plans.
 * Returns grid format (flat fields) for use in the PlanDesigner grid.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  RfpAnnualOpenEnrollmentEnum,
} from "@platform/openapi-types";

import { RfpBenefitPlan } from "../../types/rfp.api.types";

import type { DentalHmoBenefitGrid } from "./types";

/**
 * Dental HMO benefit plan structure for skeleton
 */
export interface DentalHmoBenefitPlanSkeleton {
  identifier: string;
  name: string;
  numberOfLives: number;
  type: "hmo";
  policyPeriod: BaseBenefitPlanPolicyPeriod;
  fundingType: "fullyInsured";
  participationRequirements: string;
  rateGuarantee?: RfpBenefitPlan["rateGuarantee"];
  rateCap: string;
  optionName: "notApplicable";
  benefitClassAvailability: never[];
  rateSchedule: never[];
  benefits: DentalHmoBenefitGrid;
}

/**
 * Dental HMO coverage skeleton structure
 */
export interface DentalHmoCoverageSkeleton {
  type: "dental-hmo";
  status: "quoted";
  notes: string;
  benefitPlans: DentalHmoBenefitPlanSkeleton[];
}

/**
 * Creates an empty Dental HMO benefit object in grid format
 * @returns Empty DHMO benefit with all fields set to default values
 */
export const getDentalHmoBenefitSkeleton = (): DentalHmoBenefitGrid => ({
  objectType: "dental",

  // ADA Codes - all default to 0
  adaCodeD0120: 0,
  adaCodeD0150: 0,
  adaCodeD1110: 0,
  adaCodeD1120: 0,
  adaCodeD1206: 0,
  adaCodeD1351: 0,
  adaCodeD2140: 0,
  adaCodeD2150: 0,
  adaCodeD2160: 0,
  adaCodeD2161: 0,
  adaCodeD2751: 0,
  adaCodeD2752: 0,
  adaCodeD3310: 0,
  adaCodeD3320: 0,
  adaCodeD3330: 0,
  adaCodeD3410: 0,
  adaCodeD4210: 0,
  adaCodeD4341: 0,
  adaCodeD4910: 0,
  adaCodeD5110: 0,
  adaCodeD5120: 0,
  adaCodeD5213: 0,
  adaCodeD7111: 0,
  adaCodeD7140: 0,
  adaCodeD7210: 0,
  adaCodeD8080: 0,
  adaCodeD8090: 0,

  // Administrative fields
  serviceWaitingPeriods: undefined,
  serviceWaitingPeriodsMonths: undefined,
  annualOpenEnrollment: RfpAnnualOpenEnrollmentEnum.notIncluded,
  employerContribution: undefined,

  // Extended (non-v5) fields — CD-1659/CD-1660
  preventativeAdvantage: undefined,
  oralExams: undefined,
  oralExamFrequency: undefined,
  cleaningFrequency: undefined,
  bitewingXRays: undefined,
  fullMouthXRays: undefined,
  fullMouthXRaysFrequency: undefined,
  fluorideTreatments: undefined,
  amalgamSilverFillings: undefined,
  simpleExtractions: undefined,
  bridgesDentures: undefined,
  bridgesDenturesFrequency: undefined,
  crowns: undefined,
  crownsFrequency: undefined,
  tmj: undefined,
  workInProgressIncluded: undefined,
  openOrClosedContract: undefined,
  eligibilityHours: undefined,
  weightedAvgOutOfPocket: undefined,
});

/**
 * Gets the Dental HMO coverage skeleton
 * (kept for backwards compatibility with skeleton registry)
 */
export const getDentalHmoSkeleton = (): DentalHmoCoverageSkeleton => ({
  type: "dental-hmo" as const,
  status: "quoted" as const,
  notes: "",
  benefitPlans: [
    {
      identifier: "dental-hmo-plan-1",
      name: "Dental HMO Plan",
      numberOfLives: 0,
      type: "hmo" as const,
      policyPeriod: BaseBenefitPlanPolicyPeriod.newBusiness,
      fundingType: "fullyInsured" as const,
      participationRequirements: "",
      rateGuarantee: undefined,
      rateCap: "",
      optionName: "notApplicable" as const,
      benefitClassAvailability: [],
      rateSchedule: [],
      benefits: getDentalHmoBenefitSkeleton(),
    },
  ],
});
